import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Briefcase } from 'lucide-react';

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.getProjects();
      setProjects(res.data);
    } catch (e) {
      toast.error('Error al cargar proyectos');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ingrese un nombre'); return; }
    setLoading(true);
    try {
      await api.createProject({ name: name.trim() });
      toast.success('Proyecto creado');
      setName('');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear proyecto');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id, n) => {
    if (!window.confirm(`¿Eliminar proyecto "${n}"? Se desvinculará del personal asignado.`)) return;
    try {
      await api.deleteProject(id);
      toast.success('Proyecto eliminado');
      load();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="admin-projects-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <Briefcase className="h-7 w-7 text-[#DC143C]" />
              Proyectos
            </h1>
            <p className="text-gray-600 mt-1">Catálogo de proyectos de la empresa</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-project-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight">Nuevo Proyecto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="proj_name" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre</Label>
                  <Input id="proj_name" data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Planta Norte 2026" className="h-11" required />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-project-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">{loading ? 'Guardando...' : 'Guardar'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {projects.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><p>No hay proyectos registrados</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {projects.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors" data-testid="project-row">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{p.name}</td>
                      <td className="px-6 py-4 text-right">
                        <Button variant="ghost" size="sm" data-testid="delete-project-button" onClick={() => handleDelete(p.id, p.name)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
